import { User } from 'firebase/auth'
import { sumMoney } from '@/lib/money'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  CollectionReference,
  DocumentData,
  writeBatch,
  getDoc
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import {
  Compensation,
  CreateCompensationData,
  UpdateCompensationData,
  CompensationValidationError,
  BulkCompensationData
} from '@/types/compensation'
import { DriverMasterService } from './driverMasterService'
import { PeriodService } from './periodService'

/**
 * Service for managing compensation/deduction data in Firestore
 */
export class CompensationService {
  private static readonly COLLECTION_NAME = 'compensations'

  /**
   * Get reference to user's compensations collection
   */
  private static getCollection(user: User): CollectionReference<DocumentData> {
    return collection(db, 'users', user.uid, this.COLLECTION_NAME)
  }

  /**
   * Resolve vehicle ID to driver ID
   */
  private static async resolveVehicleToDriver(user: User, vehicleId: string): Promise<string> {
    try {
      const drivers = await DriverMasterService.getDrivers(user)
      const driver = drivers.find(d => d.carId === vehicleId)

      if (!driver) {
        throw new Error('Bilen är inte kopplad till någon förare')
      }

      return driver.id
    } catch (error) {
      console.error('Error resolving vehicle to driver:', error)
      throw new Error('Det gick inte att hitta förare för vald bil')
    }
  }

  /**
   * Validate compensation data
   */
  private static validateCompensationData(data: Partial<CreateCompensationData>): CompensationValidationError[] {
    const errors: CompensationValidationError[] = []

    // Either driverId or vehicleId must be provided
    if (!data.driverId && !data.vehicleId) {
      errors.push({ field: 'driverId', message: 'Förare eller bil är obligatorisk' })
    }

    if (!data.period) {
      errors.push({ field: 'period', message: 'Period är obligatorisk' })
    } else if (!/^\d{4}-\d{2}-01$/.test(data.period)) {
      errors.push({ field: 'period', message: 'Period måste vara i format yyyy-mm-01' })
    }

    if (!data.type) {
      errors.push({ field: 'type', message: 'Typ är obligatorisk' })
    }

    if (!data.applyTo) {
      errors.push({ field: 'applyTo', message: 'Tillämpning är obligatorisk' })
    }

    if (data.amount === undefined || data.amount === null) {
      errors.push({ field: 'amount', message: 'Belopp är obligatoriskt' })
    } else if (typeof data.amount !== 'number' || data.amount <= 0) {
      errors.push({ field: 'amount', message: 'Belopp måste vara ett positivt tal' })
    }

    if (!data.category) {
      errors.push({ field: 'category', message: 'Kategori är obligatorisk' })
    }

    return errors
  }

  /**
   * Convert Firestore document to Compensation object
   */
  private static convertFromFirestore(doc: any): Compensation {
    const data = doc.data()
    return {
      id: doc.id,
      driverId: data.driverId,
      vehicleId: data.vehicleId || undefined,
      period: data.period,
      type: data.type,
      applyTo: data.applyTo,
      amount: data.amount,
      category: data.category,
      comment: data.comment || undefined,
      isDeleted: data.isDeleted || false,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    }
  }

  /**
   * Create a new compensation
   */
  static async createCompensation(
    user: User,
    data: CreateCompensationData
  ): Promise<string> {
    try {
      // Validate data
      const validationErrors = this.validateCompensationData(data)
      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.map(e => e.message).join(', ')}`)
      }

      // Resolve driverId if vehicleId is provided
      let finalDriverId = data.driverId
      if (data.vehicleId && !data.driverId) {
        finalDriverId = await this.resolveVehicleToDriver(user, data.vehicleId)
      }

      // Check if period is locked
      const isLocked = await PeriodService.isPeriodLocked(user, data.period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan skapa nya ersättningar/avdrag.')
      }

      const now = Timestamp.now()
      const compensationData = {
        driverId: finalDriverId,
        vehicleId: data.vehicleId || null,
        period: data.period,
        type: data.type,
        applyTo: data.applyTo,
        amount: data.amount,
        category: data.category,
        comment: data.comment || null,
        isDeleted: false,
        createdAt: now,
        updatedAt: now
      }

      const docRef = await addDoc(this.getCollection(user), compensationData)
      console.log('Created compensation with ID:', docRef.id)
      return docRef.id

    } catch (error) {
      console.error('Error creating compensation:', error)
      throw new Error('Det gick inte att skapa ersättning/avdrag')
    }
  }

  /**
   * Create multiple compensations in a batch operation
   */
  static async createBulkCompensations(
    user: User,
    bulkData: BulkCompensationData
  ): Promise<string[]> {
    try {
      const batch = writeBatch(db)
      const now = Timestamp.now()
      const createdIds: string[] = []

      // Validate all compensations first
      const validationErrors: Array<{ index: number; errors: CompensationValidationError[] }> = []

      for (let i = 0; i < bulkData.compensations.length; i++) {
        const comp = bulkData.compensations[i]
        const compData = {
          ...comp,
          period: bulkData.period
        }
        const errors = this.validateCompensationData(compData)
        if (errors.length > 0) {
          validationErrors.push({ index: i, errors })
        }
      }

      if (validationErrors.length > 0) {
        const errorMessages = validationErrors.map(({ index, errors }) =>
          `Rad ${index + 1}: ${errors.map(e => e.message).join(', ')}`
        ).join('\n')
        throw new Error(`Valideringsfel:\n${errorMessages}`)
      }

      // Check if period is locked
      const isLocked = await PeriodService.isPeriodLocked(user, bulkData.period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan skapa nya ersättningar/avdrag.')
      }

      // Create batch operations
      for (const comp of bulkData.compensations) {
        // Resolve driverId if vehicleId is provided
        let finalDriverId = comp.driverId
        if (comp.vehicleId && !comp.driverId) {
          finalDriverId = await this.resolveVehicleToDriver(user, comp.vehicleId)
        }

        const docRef = doc(this.getCollection(user))
        const compensationData = {
          driverId: finalDriverId,
          vehicleId: comp.vehicleId || null,
          period: bulkData.period,
          type: comp.type,
          applyTo: comp.applyTo,
          amount: comp.amount,
          category: comp.category,
          comment: comp.comment || null,
          isDeleted: false,
          createdAt: now,
          updatedAt: now
        }

        batch.set(docRef, compensationData)
        createdIds.push(docRef.id)
      }

      // Commit all operations
      await batch.commit()
      console.log(`Created ${createdIds.length} compensations in batch`)

      return createdIds

    } catch (error) {
      console.error('Error creating bulk compensations:', error)
      throw new Error(error instanceof Error ? error.message : 'Det gick inte att skapa ersättningar/avdrag')
    }
  }

  /**
   * Update an existing compensation
   */
  static async updateCompensation(
    user: User,
    compensationId: string,
    data: UpdateCompensationData
  ): Promise<void> {
    try {
      // Only validate provided fields
      const validationErrors: CompensationValidationError[] = []

      if (data.amount !== undefined && (typeof data.amount !== 'number' || data.amount <= 0)) {
        validationErrors.push({ field: 'amount', message: 'Belopp måste vara ett positivt tal' })
      }

      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.map(e => e.message).join(', ')}`)
      }

      const docRef = doc(this.getCollection(user), compensationId)

      // Check if compensation exists and get period
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Ersättning/avdrag hittades inte')
      }

      const existingCompensation = existingDoc.data() as Compensation

      // Check if period is locked
      const period = existingCompensation.period
      const isLocked = await PeriodService.isPeriodLocked(user, period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan ändra ersättningar/avdrag.')
      }

      const updateData: any = {
        updatedAt: Timestamp.now()
      }

      // Only update provided fields
      if (data.type !== undefined) updateData.type = data.type
      if (data.applyTo !== undefined) updateData.applyTo = data.applyTo
      if (data.amount !== undefined) updateData.amount = data.amount
      if (data.category !== undefined) updateData.category = data.category
      if (data.comment !== undefined) updateData.comment = data.comment

      await updateDoc(docRef, updateData)

      console.log('Updated compensation:', compensationId)

    } catch (error) {
      console.error('Error updating compensation:', error)
      throw new Error('Det gick inte att uppdatera ersättning/avdrag')
    }
  }

  /**
   * Soft delete a compensation
   */
  static async deleteCompensation(user: User, compensationId: string): Promise<void> {
    try {
      const docRef = doc(this.getCollection(user), compensationId)

      // Check if compensation exists and get period
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Ersättning/avdrag hittades inte')
      }

      const existingCompensation = existingDoc.data() as Compensation

      // Check if period is locked
      const period = existingCompensation.period
      const isLocked = await PeriodService.isPeriodLocked(user, period)
      if (isLocked) {
        throw new Error('Perioden är låst. Du måste låsa upp perioden innan du kan radera ersättningar/avdrag.')
      }

      await updateDoc(docRef, {
        isDeleted: true,
        updatedAt: Timestamp.now()
      })

      console.log('Soft deleted compensation:', compensationId)

    } catch (error) {
      console.error('Error deleting compensation:', error)
      throw new Error('Det gick inte att ta bort ersättning/avdrag')
    }
  }

  /**
   * Hard delete a compensation (permanently remove from database)
   */
  static async hardDeleteCompensation(user: User, compensationId: string): Promise<void> {
    try {
      const docRef = doc(this.getCollection(user), compensationId)
      await deleteDoc(docRef)

      console.log('Hard deleted compensation:', compensationId)

    } catch (error) {
      console.error('Error hard deleting compensation:', error)
      throw new Error('Det gick inte att permanent ta bort ersättning/avdrag')
    }
  }

  /**
   * Get all compensations for a user (excluding soft deleted)
   */
  static async getCompensations(user: User): Promise<Compensation[]> {
    try {
      const q = query(
        this.getCollection(user),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => this.convertFromFirestore(doc))

    } catch (error) {
      console.error('Error fetching compensations:', error)
      // Return empty array instead of throwing to prevent page from breaking
      return []
    }
  }

  /**
   * Get compensations for a specific driver
   */
  static async getCompensationsByDriver(user: User, driverId: string): Promise<Compensation[]> {
    try {
      const q = query(
        this.getCollection(user),
        where('driverId', '==', driverId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => this.convertFromFirestore(doc))

    } catch (error) {
      console.error('Error fetching compensations by driver:', error)
      // Return empty array instead of throwing to prevent page from breaking
      return []
    }
  }

  /**
   * Soft-delete all compensations for a driver in open (unlocked) periods only.
   * Compensations in locked periods are silently skipped.
   * Used when deleting a driver to clean up their data.
   *
   * @returns Object with count of deleted compensations and skipped (locked) compensations
   */
  static async deleteCompensationsByDriverInOpenPeriods(
    user: User,
    driverId: string
  ): Promise<{ deleted: number; skippedLocked: number }> {
    try {
      const compensations = await this.getCompensationsByDriver(user, driverId)

      if (compensations.length === 0) {
        return { deleted: 0, skippedLocked: 0 }
      }

      // Get unique periods and check which are locked
      const periods = [...new Set(compensations.map(c => c.period))]
      const periodLockStatus = await Promise.all(
        periods.map(async period => ({
          period,
          isLocked: await PeriodService.isPeriodLocked(user, period)
        }))
      )
      const lockedPeriods = new Set(
        periodLockStatus.filter(p => p.isLocked).map(p => p.period)
      )

      // Separate compensations into deletable and skipped
      const toDelete = compensations.filter(c => !lockedPeriods.has(c.period))
      const skipped = compensations.filter(c => lockedPeriods.has(c.period))

      // Batch delete compensations in open periods
      if (toDelete.length > 0) {
        const batch = writeBatch(db)
        toDelete.forEach(compensation => {
          const docRef = doc(this.getCollection(user), compensation.id)
          batch.update(docRef, {
            isDeleted: true,
            updatedAt: Timestamp.now()
          })
        })
        await batch.commit()
      }

      return { deleted: toDelete.length, skippedLocked: skipped.length }
    } catch (error) {
      console.error('Error deleting compensations by driver in open periods:', error)
      throw new Error('Det gick inte att radera förarens kompensationer')
    }
  }

  /**
   * Get compensations for a specific period
   */
  static async getCompensationsByPeriod(user: User, period: string): Promise<Compensation[]> {
    try {
      const q = query(
        this.getCollection(user),
        where('period', '==', period),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => this.convertFromFirestore(doc))

    } catch (error) {
      console.error('Error fetching compensations by period:', error)
      // Return empty array instead of throwing to prevent page from breaking
      return []
    }
  }

  /**
   * Get compensations for a specific driver and period combination
   */
  static async getCompensationsByDriverAndPeriod(
    user: User,
    driverId: string,
    period?: string
  ): Promise<Compensation[]> {
    try {
      let q = query(
        this.getCollection(user),
        where('driverId', '==', driverId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc')
      )

      if (period) {
        q = query(
          this.getCollection(user),
          where('driverId', '==', driverId),
          where('period', '==', period),
          where('isDeleted', '==', false),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => this.convertFromFirestore(doc))

    } catch (error) {
      console.error('Error fetching compensations by driver and period:', error)
      // Return empty array instead of throwing to prevent page from breaking
      return []
    }
  }

  /**
   * Get total compensation/deduction amounts for a driver
   */
  static async getCompensationTotalsByDriver(
    user: User,
    driverId: string
  ): Promise<{
    totalNetEarningsCompensations: number
    totalNetEarningsDeductions: number
    totalGrossSalaryCompensations: number
    totalGrossSalaryDeductions: number
    totalNetSalaryCompensations: number
    totalNetSalaryDeductions: number
  }> {
    try {
      const compensations = await this.getCompensationsByDriver(user, driverId)

      const sumFor = (applyTo: string, type: string): number =>
        sumMoney(
          compensations
            .filter(c => c.applyTo === applyTo && c.type === type)
            .map(c => c.amount)
        )

      return {
        totalNetEarningsCompensations: sumFor('net_earnings', 'compensation'),
        totalNetEarningsDeductions: sumFor('net_earnings', 'deduction'),
        totalGrossSalaryCompensations: sumFor('gross_salary', 'compensation'),
        totalGrossSalaryDeductions: sumFor('gross_salary', 'deduction'),
        totalNetSalaryCompensations: sumFor('net_salary', 'compensation'),
        totalNetSalaryDeductions: sumFor('net_salary', 'deduction')
      }

    } catch (error) {
      console.error('Error calculating compensation totals:', error)
      throw new Error('Det gick inte att beräkna ersättnings/avdragstotaler')
    }
  }
}